import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { getSkuIdIsATBSuiteBundle } from 'pricing-pages-lib/utils/productsUtils';
import { getQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import { useSelector } from 'react-redux';
import { useAppSelector } from '../../reducers';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { productsAndPacksForQueryParamsSelector } from '../../selectors/ProductConfigsSelectors';
import { productVersionFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { currentSkuIdForProductSelector, productForSkuIdSelector, productIdForQuantityPackAndProductApiNameSelector, productVersionSelector, quantityPackApiNameForProductIdSelector, skuIdIsHubSelector } from '../../selectors/ProductsSelectors';
import { productDataSelector, usePricingLibSelector } from '../../selectors/SelectorUtils';
import { getParcelNameToProductId } from '../getParcelNameToProductId';
import { useQueryParamsEffect } from '../urls/appQueryParamUtils';

// If a customer owns a legacy product, without this check it's possible for
// planBuilder to add both the legacy sku and the current sku to
// productConfigurations, so the product shows up twice (e.g. a bundle sku)
export const currentOrOwnedSkuIdSelector = (state, {
  productAttributeFilters
}) => {
  const ownedProductVersion = productVersionFromProductInventorySelector(state, {
    productAttributeFilters
  });
  const ownedSkuId = ownedProductVersion && ownedProductVersion.skuId;
  const currentSkuId = currentSkuIdForProductSelector(state, productAttributeFilters);
  return ownedSkuId || currentSkuId;
};
export const queryParamProductConfigurationsSelector = (state, {
  queryParams
}) => {
  const productConfigurations = [];
  const {
    products,
    packs
  } = queryParams;
  const {
    apiNameToProductId
  } = productDataSelector(state);
  const stringProductConfigs = products ? typeof products === 'string' ? [products] : products : [];
  const stringQuantityPacks = packs ? typeof packs === 'string' ? [packs] : packs : [];
  const validatedQuantity = quantityString => {
    if (!quantityString) return 1;
    const quantity = Math.abs(Number.parseInt(quantityString, 10));
    return Number.isInteger(quantity) ? quantity : 1;
  };
  stringProductConfigs.forEach(entry => {
    const split = entry.toString().split('_');
    if (split.length > 0) {
      const apiName = split[0];
      const quantity = validatedQuantity(split[1]);
      if (apiNameToProductId && apiName in apiNameToProductId) {
        productConfigurations.push(createProductConfig({
          skuId: currentOrOwnedSkuIdSelector(state, {
            productAttributeFilters: {
              apiName
            }
          }),
          quantity
        }));
      }
    }
  });
  const parseSplit = split => {
    const parcelNameToProductId = getParcelNameToProductId(state);
    // backwards compatibility where url contains parcel name as part of ?packs=
    if (parcelNameToProductId && split[0] in parcelNameToProductId) {
      const [parcelName, quantityString] = split;
      const quantity = validatedQuantity(quantityString);
      const productId = parcelNameToProductId[parcelName];
      const quantityPackApiName = quantityPackApiNameForProductIdSelector(state, {
        productId
      });
      return {
        quantity,
        productId,
        quantityPackApiName
      };
    }

    // if url uses quantityPackApiName. E.g. ?packs=additional-sales-seats_sales-hub-starter_2
    const [quantityPackApiName, productApiName, quantityString] = split;
    const quantity = validatedQuantity(quantityString);
    const productId = productIdForQuantityPackAndProductApiNameSelector(state, {
      quantityPackApiName: quantityPackApiName,
      productApiName: productApiName
    });
    return {
      quantity,
      productId,
      quantityPackApiName
    };
  };
  stringQuantityPacks.forEach(entry => {
    const split = entry.toString().split('_');
    if (split.length > 0) {
      const {
        quantity,
        productId,
        quantityPackApiName
      } = parseSplit(split);
      if (quantity >= 0 && productId && quantityPackApiName) {
        const parentSkuId = currentOrOwnedSkuIdSelector(state, {
          productAttributeFilters: {
            productId
          }
        });
        const {
          quantityPackDetails
        } = productVersionSelector(state, {
          skuId: parentSkuId
        });
        const quantityPack = quantityPackDetails.find(({
          quantityPackApiName: searchQuantityPackApiName
        }) => quantityPackApiName === searchQuantityPackApiName);
        if (quantityPack) {
          const {
            skuId
          } = quantityPack;
          productConfigurations.push({
            skuId,
            quantity
          });
        }
      }
    }
  });
  return productConfigurations;
};
export const mergeDefaultAndQueryParamProductConfigureations = (state, defaultConfigurations, queryParamProductConfigurations) => {
  return [...defaultConfigurations.filter(({
    skuId
  }) => {
    const defaultProductIsSuite = getSkuIdIsATBSuiteBundle(state, {
      skuId
    });
    return queryParamProductConfigurations.every(({
      skuId: querySkuId
    }) => {
      const queryParamProductIsSuite = getSkuIdIsATBSuiteBundle(state, {
        skuId: querySkuId
      });

      // dont add the default product if a suite from the query params exists already
      if (defaultProductIsSuite && queryParamProductIsSuite) return false;
      return skuId !== querySkuId;
    });
  }), ...queryParamProductConfigurations];
};
export const useQueryParamProductConfigsOrDefault = ({
  defaultConfigurations,
  requiredProductLines
}) => {
  const queryParams = getQueryParams();
  const queryParamProductConfigurations = useAppSelector(state => hasPricingAndPurchasingSucceededSelector(state) ? queryParamProductConfigurationsSelector(state, {
    queryParams
  }) : []);
  const isMissingRequiredProductLine = useAppSelector(state => requiredProductLines.some(productLine => queryParamProductConfigurations.every(({
    skuId
  }) => {
    const isHub = skuIdIsHubSelector(state, {
      skuId
    });
    const {
      productLine: searchProductLine
    } = productForSkuIdSelector(state, {
      skuId
    });
    return productLine !== searchProductLine || !isHub;
  })));
  const filteredDefaultConfigurations = usePricingLibSelector(state => {
    if (!queryParamProductConfigurations.length || isMissingRequiredProductLine) {
      return mergeDefaultAndQueryParamProductConfigureations(state, defaultConfigurations, queryParamProductConfigurations);
    }
    return [];
  });
  if (filteredDefaultConfigurations.length) {
    return filteredDefaultConfigurations;
  }
  return queryParamProductConfigurations;
};
export const useProductConfigsQueryParamsParamsEffect = productConfigurations => {
  const {
    products,
    packs
  } = useSelector(state => productsAndPacksForQueryParamsSelector(state, {
    productConfigurations
  }));
  useQueryParamsEffect({
    products,
    packs
  });
};